import React, { useState } from 'react';

import formatFloatForDisplay from 'services/numbers/formatFloatForDisplay';
import formatGroupedNumber from 'services/numbers/formatGroupedNumber';
import isNumber from 'services/numbers/isNumber';
import numericStringToFloat from 'services/numbers/numericStringToFloat';
import {
  getSellingPriceForMarginEur,
  getSellingPriceForMarginPercent,
} from 'services/products/calculations/getSellingPriceForMargin';

type MarginField = 'eur' | 'percent';

// The minus sign of numbers formatted for Finnish (U+2212); \s also covers the spaces
// between thousands (no-break spaces)
const MINUS_SIGN = String.fromCharCode(0x2212);

interface IMarginInput {
  onBlur: () => void;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  value: string;
}

/**
 * The margin € and margin % fields of a calculation row. Typing a margin sets the selling
 * price of the row (see getSellingPriceForMargin); the other fields are calculated from the
 * selling price as before. The typed text stays in the field until it is left, so the
 * rounding of the selling price does not change what is being typed.
 */
const useMarginInputs = ({
  contributionMargin,
  contributionMarginRatio,
  expense,
  quantityFactor,
  setSellingPrice,
}: {
  // The margin and the margin % of the row as calculated now
  contributionMargin: number;
  contributionMarginRatio: number;
  // The expense of the row and the factor its selling price is multiplied by
  expense: number;
  quantityFactor: number;
  setSellingPrice: (sellingPrice: string) => void;
}): { eur: IMarginInput; percent: IMarginInput } => {
  const [typed, setTyped] = useState<{
    field: MarginField;
    text: string;
  } | null>(null);

  const getHandler =
    (field: MarginField) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const text = event.target.value;
      setTyped({ field, text });

      // The shown margins have spaces between thousands and a minus sign (−) of their
      // own, e.g. "−1 798,46"; they are removed so an edited margin is read correctly
      const cleaned = text.replace(/\s/g, '').split(MINUS_SIGN).join('-');

      // A partly typed number (e.g. "-") does not change the price yet
      if (!isNumber(cleaned)) {
        return;
      }

      const margin = numericStringToFloat(cleaned);
      const sellingPrice =
        field === 'eur'
          ? getSellingPriceForMarginEur(expense, margin, quantityFactor)
          : getSellingPriceForMarginPercent(expense, margin, quantityFactor);

      if (sellingPrice !== null) {
        setSellingPrice(formatFloatForDisplay(sellingPrice));
      }
    };

  const getInput = (field: MarginField, value: number): IMarginInput => ({
    onBlur: () => setTyped(null),
    onChange: getHandler(field),
    value:
      typed !== null && typed.field === field
        ? typed.text
        : formatGroupedNumber(value),
  });

  return {
    eur: getInput('eur', contributionMargin),
    percent: getInput('percent', contributionMarginRatio),
  };
};

export default useMarginInputs;
